import Environment from "./baseUrl";
import axios from "axios";
import { AUTH_ERROR_STORAGE_KEY } from "../configs/constants";
import { getTenantSlug } from "./tenant";

const addTenantHeader = (headers) => {
  const tenantSlug = getTenantSlug();

  if (tenantSlug) {
    headers["X-Company"] = tenantSlug;
  }

  return headers;
};

const getHeaders = async (token, isFileUpload = false) => {
  const headers = {
    Authorization: token ? "Bearer " + token : undefined,
    "Content-Type": isFileUpload ? undefined : "application/json",
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
  };
  if (!token) {
    delete headers.Authorization;
  }
  if (isFileUpload) {
    delete headers["Content-Type"];
  }
  return addTenantHeader(headers);
};

const getProfilePictureUploadHeaders = (token) => {
  if (token) {
    return addTenantHeader({
      Authorization: "Bearer " + token,
      "Content-Type": "multipart/form-data",
      Accept: "application/json",
      "Access-Control-Allow-Origin": "*",
    });
  } else {
    return addTenantHeader({
      Authorization: Environment.BasicToken,
      "Content-Type": "application/json",
      Accept: "application/json",
      "Access-Control-Allow-Origin": "*",
    });
  }
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const skipRedirect = error?.config?.skipAuthRedirect === true;
    const isTenantMismatch =
      status === 409 &&
      error?.response?.data?.data?.code === "tenant_mismatch";

    if (isTenantMismatch) {
      localStorage.removeItem("last_visited_page");
      localStorage.removeItem("access_token");
      window.location = "/login";
      return Promise.reject(error);
    }

    if (status === 401 && skipRedirect) {
      return Promise.reject(error);
    }

    if (status === 401) {
      const responseData = error?.response?.data || {};
      const storedError = {
        message: responseData?.message,
        errors: responseData?.errors || responseData?.data?.errors,
      };

      try {
        sessionStorage.setItem(
          AUTH_ERROR_STORAGE_KEY,
          JSON.stringify(storedError),
        );
      } catch {}

      localStorage.removeItem("last_visited_page");
      localStorage.removeItem("access_token");
      window.location = "/login";
    }

    return Promise.reject(error);
  },
);

export const doPost = async (
  url,
  data,
  token,
  isFileUpload = false,
  options = {},
) => {
  const headers = token
    ? await getHeaders(token, isFileUpload)
    : await getHeaders();

  return axios({
    method: "POST",
    url: Environment.API_BASE_URL + url,
    data,
    headers,
    ...options,
  });
};

export const doPatch = async (url, data, token) => {
  return axios({
    method: "PATCH",
    url: Environment.API_BASE_URL + url,
    data,
    headers: await getHeaders(token),
  });
};

export const doPut = async (url, data, token) => {
  return axios({
    method: "PUT",
    url: Environment.API_BASE_URL + url,
    data,
    headers: await getHeaders(token),
  });
};

export const doGet = async (url, token, data, options = {}) => {
  const headers = await getHeaders(token);
  let finalUrl = url;

  if (data) {
    const params = Object.keys(data)
      .map((key) => key + "=" + encodeURIComponent(data[key]))
      .join("&");
    if (params) finalUrl += "?" + params;
  }

  return axios({
    method: "GET",
    url: Environment.API_BASE_URL + finalUrl,
    headers,
    ...options,
  });
};

export const doDelete = async (url, data, token) => {
  return axios({
    method: "DELETE",
    url: Environment.API_BASE_URL + url,
    data,
    headers: await getHeaders(token),
  });
};

export const doPostImageUpload = (url, data, token) => {
  return axios({
    method: "POST",
    url: Environment.API_BASE_URL + url,
    data,
    headers: getProfilePictureUploadHeaders(token),
  });
};

const parseDownloadResponseData = async (data) => {
  if (typeof Blob === "undefined" || !(data instanceof Blob)) {
    return data;
  }

  const responseText = await data.text();
  if (!responseText) return {};

  try {
    return JSON.parse(responseText);
  } catch {
    return { message: responseText };
  }
};

export const doDownload = async (
  url,
  token,
  exportFileName = "Abc.txt",
  data,
) => {
  const headers = await getHeaders(token);
  if (data) {
    var params = Object.keys(data)
      .map((key) => key + "=" + data[key])
      .join("&");

    if (params) {
      url = url + "?" + params;
    }
  }
  return axios({
    method: "GET",
    url: Environment.API_BASE_URL + url,
    data: null,
    headers,
    responseType: "blob",
  })
    .then(async (response) => {
      const contentType = response?.headers?.["content-type"] || "";

      if (contentType.toLowerCase().includes("json")) {
        const responseData = await parseDownloadResponseData(response.data);
        const downloadError = new Error(
          responseData?.message || "Download failed",
        );
        downloadError.response = {
          ...response,
          data: responseData,
        };
        throw downloadError;
      }

      const downloadUrl = window.URL.createObjectURL(
        new Blob([response.data]),
      );
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", exportFileName);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0);

      return response;
    })
    .catch(async (error) => {
      if (error?.response?.data) {
        error.response.data = await parseDownloadResponseData(
          error.response.data,
        );
      }

      return Promise.reject(error);
    });
};
